import { useEffect, useState, useRef } from 'react';
import { Bell, LogOut, User, Activity } from 'lucide-react';
import api from '@/lib/api';

/** Groww-style sticky mobile header: logo + bell + avatar.
 *  Only visible on mobile viewports (<md). Desktop keeps its own header.
 */
export const MobileHeader = ({ userName = '', onBellClick, onProfileClick, onLogout, onSpotClick, onFuturesClick }) => {
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let active = true;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/notifications/unread-count');
        if (active) setUnread(Number(data?.unread_count ?? data?.count ?? 0));
      } catch (_) {}
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const initials = (userName || 'U').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header
      className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-brand-border px-4 py-2.5 flex items-center justify-between"
      style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))' }}
      data-testid="mobile-header"
    >
      {/* Left — logo */}
      <div className="flex items-center gap-2">
        <img
          src="/quantora-logo.png"
          alt="Quantora"
          className="w-9 h-9 object-contain"
          data-testid="mobile-header-logo"
        />
        <div className="leading-tight">
          <p className="font-heading text-sm font-bold text-brand-text-primary">Quantora</p>
          <p className="text-[9px] uppercase tracking-widest text-brand-text-secondary -mt-0.5">Investor Desk</p>
        </div>
      </div>

      {/* Right — quick trading launcher + bell + avatar */}
      <div className="flex items-center gap-2">
        <button
          onClick={onSpotClick}
          className="h-8 px-2.5 rounded-full bg-brand-primary text-white text-[11px] font-semibold flex items-center gap-1 active:scale-95 transition-all"
          data-testid="mobile-header-spot"
          aria-label="Open spot trading"
        >
          <Activity size={11} /> Spot
        </button>
        <button
          onClick={onFuturesClick}
          className="h-8 px-2.5 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center gap-1 active:scale-95 transition-all"
          data-testid="mobile-header-futures"
          aria-label="Open futures trading"
        >
          <Activity size={11} /> Futures
        </button>

        <button
          onClick={onBellClick}
          className="relative w-9 h-9 rounded-full bg-brand-bg hover:bg-brand-surface-hover border border-brand-border flex items-center justify-center transition-all active:scale-95"
          data-testid="mobile-header-bell"
          aria-label="Notifications"
        >
          <Bell size={16} className="text-brand-text-primary" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
              data-testid="mobile-header-bell-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {/* Avatar + dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-primary to-brand-primary-hover text-white font-bold text-xs flex items-center justify-center active:scale-95 transition-all shadow-sm"
            data-testid="mobile-header-avatar"
            aria-label="Open account menu"
          >
            {initials || <User size={14} />}
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-11 w-52 bg-white border border-brand-border rounded-xl shadow-lg py-2 z-50"
              data-testid="mobile-header-menu"
            >
              <div className="px-3 py-2 border-b border-brand-border">
                <p className="text-xs font-semibold text-brand-text-primary truncate">{userName || 'Investor'}</p>
                <p className="text-[10px] text-brand-text-secondary">Signed in</p>
              </div>
              <button
                onClick={() => { setMenuOpen(false); onProfileClick?.(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-brand-text-primary hover:bg-brand-bg transition-colors"
                data-testid="mobile-header-menu-profile"
              >
                <User size={14} /> Profile & Banking
              </button>
              <button
                onClick={() => { setMenuOpen(false); onLogout?.(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50 transition-colors"
                data-testid="mobile-header-menu-logout"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
